import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { MegaphoneIcon, TrashIcon } from "@phosphor-icons/react";

export default function AdminSponsorships() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    target_type: "listing",
    target_id: "",
    duration_days: 7,
    rank: 3,
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/sponsorships");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Failed to load sponsorships");
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.target_id.trim()) return toast.error("Target ID required");
    setSaving(true);
    try {
      await api.post("/admin/sponsorships", {
        ...form,
        duration_days: Number(form.duration_days),
        rank: Number(form.rank),
      });
      toast.success("Sponsorship created");
      setForm({ ...form, target_id: "", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
    setSaving(false);
  };

  const onRemove = async (row) => {
    if (!window.confirm(`Remove sponsorship on ${row.target_type} ${row.target_id}?`)) return;
    try {
      await api.delete(`/admin/sponsorships/${row.target_type}/${row.target_id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="admin-sponsorships-page">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-10">
        <p className="overline text-zinc-500 flex items-center gap-2">
          <MegaphoneIcon size={14} /> ADMIN · SPONSORSHIPS
        </p>
        <h1 className="font-display font-black text-3xl tracking-tighter mt-2">
          Paid placements
        </h1>
        <p className="text-sm text-zinc-600 mt-1">
          Boost a listing or live stream to the top of browse/live feeds. Users see a "Sponsored" pill.
        </p>

        <form
          onSubmit={onCreate}
          className="mt-8 rounded-2xl border border-zinc-200 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3"
          data-testid="sponsor-create-form"
        >
          <label className="text-sm">
            Type
            <select
              className="mt-1 w-full border border-zinc-300 rounded-lg p-2"
              value={form.target_type}
              onChange={(e) => setForm({ ...form, target_type: e.target.value })}
              data-testid="sponsor-type"
            >
              <option value="listing">Listing</option>
              <option value="stream">Stream</option>
            </select>
          </label>
          <label className="text-sm">
            Target ID
            <input
              className="mt-1 w-full border border-zinc-300 rounded-lg p-2 font-mono text-xs"
              placeholder="lst_abc123 or stream_xyz"
              value={form.target_id}
              onChange={(e) => setForm({ ...form, target_id: e.target.value })}
              data-testid="sponsor-target-id"
            />
          </label>
          <label className="text-sm">
            Duration (days)
            <input
              type="number"
              min={1}
              max={60}
              className="mt-1 w-full border border-zinc-300 rounded-lg p-2"
              value={form.duration_days}
              onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
              data-testid="sponsor-duration"
            />
          </label>
          <label className="text-sm">
            Rank (1–10)
            <input
              type="number"
              min={1}
              max={10}
              className="mt-1 w-full border border-zinc-300 rounded-lg p-2"
              value={form.rank}
              onChange={(e) => setForm({ ...form, rank: e.target.value })}
              data-testid="sponsor-rank"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            Note (admin-only)
            <input
              className="mt-1 w-full border border-zinc-300 rounded-lg p-2"
              placeholder="e.g. Paid package $50"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="sponsor-note"
            />
          </label>
          <div className="sm:col-span-2">
            <button
              disabled={saving}
              className="bg-[#1D78AB] hover:bg-[#0F3C57] text-white px-5 py-2 rounded-full font-bold disabled:opacity-50"
              data-testid="sponsor-submit"
            >
              {saving ? "Creating…" : "Create sponsorship"}
            </button>
          </div>
        </form>

        <h2 className="overline text-zinc-500 mt-10">ACTIVE · RECENT</h2>
        <div className="mt-3 border border-zinc-200 rounded-2xl overflow-hidden divide-y divide-zinc-100">
          {items.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">No sponsorships yet.</div>
          ) : (
            items.map((row) => (
              <div
                key={row.id}
                className="p-4 flex items-center gap-4 text-sm"
                data-testid={`sponsor-row-${row.id}`}
              >
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-black tracking-widest ${
                    row.active ? "bg-amber-500 text-white" : "bg-zinc-200 text-zinc-700"
                  }`}
                >
                  {row.active ? "ACTIVE" : "ENDED"}
                </span>
                <span className="text-xs text-zinc-500 uppercase">{row.target_type}</span>
                <span className="font-mono text-xs truncate flex-1">{row.target_id}</span>
                <span className="text-xs text-zinc-500">
                  rank {row.rank} · {row.duration_days}d
                </span>
                {row.active && (
                  <button
                    onClick={() => onRemove(row)}
                    className="text-rose-600 hover:text-rose-700"
                    aria-label="Remove"
                    data-testid={`sponsor-remove-${row.id}`}
                  >
                    <TrashIcon size={18} />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
